import React, { useState } from 'react';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import CheckIcon from '@mui/icons-material/Check';
import StarIcon from '@mui/icons-material/Star';

const ACCENT = '#03c0ff';
const ACCENT_ORANGE = '#fc8d05';

const plans = [
  {
    name: 'Başlangıç',
    description: 'Küçük dernekler için temel özellikler',
    monthlyPrice: 299,
    yearlyPrice: 249,
    features: [
      'Sınırsız üye kaydı',
      'Aidat takibi',
      'Temel raporlama',
      'E-posta bildirimleri',
      '1 GB depolama',
      'E-posta desteği',
    ],
    cta: 'Başla',
    popular: false,
  },
  {
    name: 'Profesyonel',
    description: 'Büyüyen dernekler için gelişmiş özellikler',
    monthlyPrice: 599,
    yearlyPrice: 499,
    features: [
      'Başlangıç planındaki her şey',
      'Widget sistemi',
      'Etkinlik yönetimi',
      'SMS bildirimleri',
      'Gelişmiş raporlar',
      '10 GB depolama',
      'Öncelikli destek',
      'Mobil uygulama',
    ],
    cta: 'Başla',
    popular: true,
  },
  {
    name: 'Kurumsal',
    description: 'Büyük organizasyonlar için tam çözüm',
    monthlyPrice: 999,
    yearlyPrice: 849,
    features: [
      'Profesyonel planındaki her şey',
      'Sınırsız depolama',
      'API erişimi',
      'Özel entegrasyonlar',
      'Çoklu şube yönetimi',
      'Özel eğitim',
      '7/24 telefon desteği',
      'SLA garantisi',
    ],
    cta: 'İletişime Geç',
    popular: false,
  },
];

const PricingCard = ({ plan, isYearly, index }) => {
  const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  const savings = plan.monthlyPrice - plan.yearlyPrice;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      style={{ height: '100%' }}
    >
      <Box
        sx={{
          height: '100%',
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          background: plan.popular ? '#0f172a' : '#fff',
          border: plan.popular ? 'none' : '1px solid #e2e8f0',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          transform: plan.popular ? 'scale(1.02)' : 'scale(1)',
          boxShadow: plan.popular 
            ? '0 25px 50px -12px rgba(0,0,0,0.25)' 
            : '0 4px 20px rgba(0,0,0,0.04)',
          '&:hover': {
            transform: plan.popular ? 'scale(1.04)' : 'scale(1.02)',
            boxShadow: plan.popular 
              ? '0 30px 60px -12px rgba(0,0,0,0.3)' 
              : '0 12px 32px rgba(0,0,0,0.08)',
          },
        }}
      >
        {/* Popular Badge */}
        {plan.popular && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              background: ACCENT,
              color: '#fff',
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            <StarIcon sx={{ fontSize: 14 }} />
            Popüler
          </Box>
        )}

        {/* Plan Name */}
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: 700,
            color: plan.popular ? '#fff' : '#0f172a',
            mb: 0.5,
          }}
        >
          {plan.name}
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            fontSize: '0.9rem',
            color: plan.popular ? 'rgba(255,255,255,0.7)' : '#64748b',
            mb: 3,
          }}
        >
          {plan.description}
        </Typography>

        {/* Price */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <AnimatePresence mode="wait">
              <motion.span
                key={price}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2 }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    fontWeight: 800,
                    color: plan.popular ? '#fff' : '#0f172a',
                    letterSpacing: '-0.02em',
                  }}
                >
                  ₺{price}
                </Typography>
              </motion.span>
            </AnimatePresence>
            <Typography
              component="span"
              sx={{
                fontSize: '1rem',
                color: plan.popular ? 'rgba(255,255,255,0.6)' : '#94a3b8',
              }}
            >
              /ay
            </Typography>
          </Box>

          {/* Yearly Savings */}
          {isYearly && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  color: ACCENT_ORANGE,
                  fontWeight: 600,
                  mt: 0.5,
                }}
              >
                Yıllık ₺{savings * 12} tasarruf
              </Typography>
            </motion.div>
          )}
        </Box>

        {/* CTA Button */}
        <Button
          fullWidth
          variant={plan.popular ? 'contained' : 'outlined'}
          sx={{
            py: 1.5,
            mb: 3,
            borderRadius: 2,
            fontWeight: 600,
            fontSize: '0.95rem',
            textTransform: 'none',
            background: plan.popular ? ACCENT : 'transparent',
            borderColor: plan.popular ? ACCENT : '#e2e8f0',
            color: plan.popular ? '#fff' : '#0f172a',
            '&:hover': {
              background: plan.popular ? '#02a8e0' : 'rgba(3,192,255,0.08)',
              borderColor: ACCENT,
            },
          }}
        >
          {plan.cta}
        </Button>

        {/* Features */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {plan.features.map((feature, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <CheckIcon
                sx={{
                  fontSize: 18,
                  color: plan.popular ? ACCENT : '#10b981',
                  mt: 0.25,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.9rem',
                  color: plan.popular ? 'rgba(255,255,255,0.85)' : '#475569',
                  lineHeight: 1.5,
                }}
              >
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </motion.div>
  );
};

const PricingSectionNew = () => {
  const [isYearly, setIsYearly] = useState(true);

  return (
    <Box
      sx={{
        py: { xs: 10, md: 16 },
        background: '#f8fafc',
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
            <Typography
              sx={{
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                fontWeight: 800,
                color: '#0f172a',
                letterSpacing: '-0.02em',
                mb: 2,
              }}
            >
              Basit ve Şeffaf Fiyatlandırma
            </Typography>
            <Typography
              sx={{
                color: '#64748b',
                fontSize: { xs: '1rem', md: '1.1rem' },
                maxWidth: 500,
                mx: 'auto',
                mb: 4,
              }}
            >
              Gizli ücret yok. İstediğiniz zaman iptal edebilirsiniz.
            </Typography>

            {/* Toggle */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                p: 0.75,
                borderRadius: 3,
                background: '#fff',
                border: '1px solid #e2e8f0',
              }}
            >
              <Box
                onClick={() => setIsYearly(false)}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  background: !isYearly ? ACCENT : 'transparent',
                  color: !isYearly ? '#fff' : '#64748b',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              >
                Aylık
              </Box>
              <Box
                onClick={() => setIsYearly(true)}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  background: isYearly ? ACCENT : 'transparent',
                  color: isYearly ? '#fff' : '#64748b',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                Yıllık
                <Box
                  component="span"
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    background: isYearly ? 'rgba(255,255,255,0.2)' : ACCENT_ORANGE,
                    color: '#fff',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                  }}
                >
                  %17 İndirim
                </Box>
              </Box>
            </Box>
          </Box>
        </motion.div>

        {/* Pricing Cards */}
        <Grid container spacing={3} alignItems="stretch">
          {plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <PricingCard plan={plan} isYearly={isYearly} index={index} />
            </Grid>
          ))}
        </Grid>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', mt: { xs: 5, md: 8 } }}>
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.9rem',
              }}
            >
              Tüm planlar 14 gün ücretsiz deneme içerir. Kredi kartı gerekmez.
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default PricingSectionNew;
